import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles, useMediaQuery, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Typography } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import { spacing50 } from '@ellucian/react-design-system/core/styles/tokens';
import useApplicationForm from '../useApplicationForm';
import { useDocumentGroups } from './useDocumentGroups';
import { useApi } from '../../ApiContext/ApiContext';
import { useSnackbar } from '../../SnackbarContext/SnackbarContext';
import { setApplicationValue } from '../redux/slices/applicationSlice';
import { setSettingsValue } from '../redux/slices/settingsSlice';
import { 
    generateTempId,
    validateFile as validateFileHelper,
} from '../../../utilities/fileHelpers';
import { CONSTANTS } from '../../Helpers/Configuration';
import { normalizeFileMetadata } from '../../../utilities/fileUploadService';
import { FormattedMessage } from 'react-intl';
import { stripDocumentIdFromName } from '../../../shared/fileNameUtils';
import DocumentGroupTable from './DocumentGroupTable';
import DocumentGroupCards from './DocumentGroupCards';
import EmptyState from './EmptyState';

const SUBMITTED_DOCS_CACHE_PREFIX = 'submittedDocs.file.';

const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};

const resolveDownloadFileName = (file = {}) => {
    const documentId = file.documentId || file.id || file.fileId;
    const rawName = file.fileName || file.name || file.originalName || '';
    const displayName = stripDocumentIdFromName(rawName, documentId) || rawName;

    return displayName || `document_${Date.now()}`;
};

const useStyles = makeStyles({
    name: 'index'
})(() => ({
    root: {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridGap: spacing50,
    },
    pageDivider: {
        margin: [0, '!important'],
    },
}));

const selectApplicationId = state => state.application?.id;
const selectSupplementalItems = state => state.application?.supplementalItems || {};
const selectProspect = state => state.application?.prospect;
const selectDocumentsHasChanges = state => state.settings?.supplementalDocumentsHasChanges || false;
const selectApplicantName = state => {
    // Try to get name from application.prospect first
    let firstName = state.application?.prospect?.firstName || '';
    let lastName = state.application?.prospect?.lastName || '';
    
    // Fall back to application.name (EEDM structure)
    if (!firstName && !lastName) {
        firstName = state.application?.name?.firstName || '';
        lastName = state.application?.name?.lastName || '';
    }
    
    // If still not found, try to get from application.values (personalization fields)
    if (!firstName && !lastName) {
        const values = state.application?.values || {};
        // Search through all page values for first-name and last-name
        Object.values(values).forEach(pageValues => {
            if (pageValues && typeof pageValues === 'object') {
                firstName = firstName || pageValues['first-name'] || '';
                lastName = lastName || pageValues['last-name'] || '';
            }
        });
    }
    
    return `${firstName} ${lastName}`.trim() || '';
};

const SubmittedDocuments = () => {
    const { classes } = useStyles();
    const intl = useIntl();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width:600px)');
    const { template, selectedLocale } = useApplicationForm();
    const api = useApi();
    const snackbar = useSnackbar();
    const applicationId = useSelector(selectApplicationId);
    const prospect = useSelector(selectProspect);
    const supplementalItems = useSelector(selectSupplementalItems);
    const applicantName = useSelector(selectApplicantName);
    const hasChanges = useSelector(selectDocumentsHasChanges);
    const questionGroups = useDocumentGroups(template);
    const [expandedRows, setExpandedRows] = useState({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const fileInputRef = useRef(null);
    const currentUploadQuestionRef = useRef(null);

    useEffect(() => {
        // Check if there are any files in any question group
        const hasAnyFiles = Object.values(supplementalItems).some(item => 
            item?.files && Array.isArray(item.files) && item.files.length > 0
        );
        
        // This handles the case where files exist from a previous session
        if (hasAnyFiles && !hasChanges) {
            dispatch(setSettingsValue({
                name: 'supplementalDocumentsHasChanges',
                value: true
            }));
        }
    }, [supplementalItems, hasChanges, dispatch]);

    const handleRowClick = useCallback((index) => {
        setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
    }, []);

    const handleDeleteFile = useCallback((file, questionId) => {
        setFileToDelete({ ...file, questionId });
        setDeleteDialogOpen(true);
    }, []);

    const handleViewFile = useCallback(async (file) => {
        const edmFileId = file.edmFileId || file.fileId || file.id;
        if (!edmFileId) {
            snackbar.error(intl.formatMessage({ id: 'supportingDocumentsFileNotFound' }));
            return;
        }

        try {
            const fileName = resolveDownloadFileName(file);
            const cacheKey = `${SUBMITTED_DOCS_CACHE_PREFIX}${edmFileId}`;
            
            let base64Content;
            try {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    base64Content = cached;
                } else {
                    const response = await fetch(`/api/edm/storage/files/${edmFileId}/content`);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch file: ${response.statusText}`);
                    }
                    const arrayBuffer = await response.arrayBuffer();
                    base64Content = arrayBufferToBase64(arrayBuffer);
                    // Audit is handled server-side via the EDM proxy route
                    try {
                        localStorage.setItem(cacheKey, base64Content);
                    } catch (_error) {
                        // Silently fail - cache not critical
                    }
                }
            } catch (_cacheError) {
                // Fallback to direct fetch
                const response = await fetch(`/api/edm/storage/files/${edmFileId}/content`);
                if (!response.ok) {
                    throw new Error(`Failed to fetch file: ${response.statusText}`);
                }
                const arrayBuffer = await response.arrayBuffer();
                base64Content = arrayBufferToBase64(arrayBuffer);
            }

            if (base64Content) {
                const binaryString = atob(base64Content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: file.mimeType || 'application/pdf' });
                
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (_error) {
            snackbar.error(intl.formatMessage({ id: 'supportingDocumentsDownloadError' }));
        }
    }, [snackbar, intl]);

    const handleUploadFile = useCallback((questionId) => {
        currentUploadQuestionRef.current = questionId;
        
        // Open file picker
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleFileChange = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        
        if (files.length === 0 || !currentUploadQuestionRef.current) {
            return;
        }

        const questionId = currentUploadQuestionRef.current;
        const file = files[0];
        
        const question = questionGroups.find(q => q.questionId === questionId);
        if (!question) {
            snackbar.error(intl.formatMessage({ id: 'supportingDocumentsQuestionNotFound' }));
            event.target.value = '';
            return;
        }

        const maxSizeMB = question.fileSizeLimit || CONSTANTS.supplementalQuestionDetails.fileSizeLimit;
        const allowedTypes = question.allowedFileTypes || CONSTANTS.allowedFileTypes;
        const maxFiles = question.fileNumberLimit || CONSTANTS.supplementalQuestionDetails.fileDefaultLimit;
        
        const currentQuestionEntry = supplementalItems[questionId] || {};
        const currentFiles = Array.isArray(currentQuestionEntry.files) ? currentQuestionEntry.files : [];
        
        const activeFiles = currentFiles.filter(f => f?.status !== 'deleted' && f?.status !== 'error');
        const activeFileCount = activeFiles.length;
        
        if (activeFileCount >= maxFiles) {
            const errorMsg = intl.formatMessage(
                { id: 'uploadQuestionMaxFilesErrorTextId' },
                { max: maxFiles }
            );
            snackbar.error(errorMsg);
            event.target.value = '';
            return;
        }
        
        const existingFileNames = activeFiles
            .map(f => f?.name)
            .filter(Boolean);

        const result = validateFileHelper(file, {
            maxSizeMB,
            allowedTypes,
            existingFileNames
        });

        if (!result.valid) {
            let errorId = 'uploadQuestionFileErrorTextId';
            const params = { fileName: file.name };

            if (result.error?.includes('exceeds maximum size')) {
                errorId = 'uploadQuestionFileSizeErrorTextId';
                params.maxSize = maxSizeMB;
            } else if (result.error?.includes('not allowed')) {
                errorId = 'uploadQuestionFileTypeErrorTextId';
                params.fileName = file.name;
            } else if (result.error?.includes('already exists')) {
                errorId = 'uploadQuestionDuplicateFileErrorTextId';
                params.fileName = file.name;
            }

            snackbar.error(intl.formatMessage({ id: errorId }, params));
            event.target.value = '';
            return;
        }

        const documentId = generateTempId();

        try {
            const initialMetadata = {
                id: documentId,
                documentId,
                name: file.name,
                size: file.size,
                mimeType: file.type,
                uploadedAt: new Date().toISOString(),
                status: 'uploading',
                progress: 0
            };

            // Show uploading state
            const updatedFiles = [...currentFiles, initialMetadata];
            dispatch(setApplicationValue({
                name: `supplementalItems.${questionId}`,
                value: {
                    ...currentQuestionEntry,
                    files: updatedFiles
                }
            }));

            const edmMetadata = {
                name: file.name,
                mimeType: file.type,
                documentId
            };
            if (applicationId) edmMetadata.appId = applicationId;
            if (prospect) edmMetadata.prospectId = prospect?.id || prospect;

            if (!api?.edm?.uploadFileChunked) {
                throw new Error('EDM upload method not available');
            }

            const edmResponse = await api.edm.uploadFileChunked(file, edmMetadata, {
                chunkSize: 5 * 1024 * 1024,
                concurrency: 3,
                retryCount: 3,
                onProgress: (progress) => {
                    dispatch(setApplicationValue({
                        name: `supplementalItems.${questionId}`,
                        value: {
                            ...currentQuestionEntry,
                            files: currentFiles.map(f =>
                                f.documentId === documentId 
                                    ? { ...initialMetadata, progress: progress?.percentage || 0 }
                                    : f
                            ).concat(
                                !currentFiles.find(f => f.documentId === documentId)
                                    ? [{ ...initialMetadata, progress: progress?.percentage || 0 }]
                                    : []
                            )
                        }
                    }));
                }
            });

            const edmFileId = edmResponse?.edmFileId || edmResponse?.id || edmResponse?.fileId || edmResponse?.data?.id;

            if (!edmFileId) {
                throw new Error(intl.formatMessage({ id: 'uploadQuestionServerErrorTextId' }));
            }

            const normalizedMetadata = normalizeFileMetadata({ 
                ...file, 
                documentId 
            }, edmResponse);
            normalizedMetadata.edmFileId = edmFileId;
            normalizedMetadata.status = 'success';
            normalizedMetadata.uploadedAt = new Date().toISOString();

            if (applicationId) {
                const storeResponse = await api.supplementalItems.storeUploadedDocument(
                    applicationId,
                    {
                        questionId,
                        documentId,
                        edmFileId,
                        fileName: file.name,
                        fileSize: file.size,
                        mimeType: file.type
                    }
                );

                const currentEntry = supplementalItems[questionId] || {};
                dispatch(setApplicationValue({
                    name: `supplementalItems.${questionId}`,
                    value: {
                        ...currentEntry,
                        ...storeResponse
                    }
                }));
            }
            dispatch(setSettingsValue({
                name: 'supplementalDocumentsHasChanges',
                value: true
            }));
            snackbar.success(
                intl.formatMessage({ id: 'uploadSuccessTextId' })
            );
        } catch (_error) {
            const errorMsg = intl.formatMessage({ id: 'uploadQuestionServerErrorTextId' });

            const updatedFilesWithError = currentFiles.map(f =>
                f.documentId === documentId 
                    ? { ...f, status: 'error', errorMessage: errorMsg }
                    : f
            ).concat(
                !currentFiles.find(f => f.documentId === documentId)
                    ? [{
                        id: documentId,
                        documentId,
                        name: file.name,
                        size: file.size,
                        mimeType: file.type,
                        status: 'error',
                        errorMessage: errorMsg,
                        uploadedAt: new Date().toISOString()
                    }]
                    : []
            );

            dispatch(setApplicationValue({
                name: `supplementalItems.${questionId}`,
                value: {
                    ...currentQuestionEntry,
                    files: updatedFilesWithError
                }
            }));

            snackbar.error(errorMsg);
        } finally {
            event.target.value = '';
            currentUploadQuestionRef.current = null;
        }
    }, [questionGroups, supplementalItems, applicationId, prospect, api, dispatch, snackbar, intl]);

    const closeDeleteDialog = useCallback(() => {
        setDeleteDialogOpen(false);
        setFileToDelete(null);
    }, []);

    const handleSendMessage = useCallback(async (questionId, message) => {
        if (!applicationId || !questionId || !message?.trim()) return;

        try {
            await api.supplementalItems.addApplicantComment(applicationId, questionId, message.trim());
            
            // Update local state with new comment
            const currentQuestionEntry = supplementalItems[questionId];
            if (currentQuestionEntry) {
                const newComment = {
                    comment: message.trim(),
                    createdAt: new Date().toISOString(),
                    createdBy: 'applicant',
                    isApplicantComment: true,
                    applicantName: applicantName
                };
                const updatedComments = [
                    ...(currentQuestionEntry.documentReviewComments || []),
                    newComment
                ];
                
                // Update supplementalItems with new comments
                const updatedSupplementalItems = {
                    ...supplementalItems,
                    [questionId]: {
                        ...currentQuestionEntry,
                        documentReviewComments: updatedComments
                    }
                };
                
                dispatch(setApplicationValue({
                    name: 'supplementalItems',
                    value: updatedSupplementalItems
                }));
            }
            
            snackbar.success(intl.formatMessage({ id: 'applicantMessageSentSuccess' }));
        } catch (error) {
            snackbar.error(intl.formatMessage({ id: 'applicantMessageSentError' }));
            throw error;
        }
    }, [applicationId, api, supplementalItems, applicantName, dispatch, snackbar, intl]);

    const handleResubmitQuestion = useCallback(async (questionId) => {
        if (!applicationId || !questionId) return;

        // Check if there are any files to submit
        const questionEntry = supplementalItems[questionId];
        const hasFiles = questionEntry?.files && Array.isArray(questionEntry.files) && 
                         questionEntry.files.some(f => f?.status !== 'deleted' && f?.status !== 'error');
        
        if (!hasFiles) {
            snackbar.error(intl.formatMessage({ id: 'supplementQuestionNoFilesError' }));
            return;
        }

        try {
            const result = await api.supplementalItems.resubmitSupplementalQuestion(applicationId, questionId);
            
            // Update Redux with the new status
            const currentQuestionEntry = supplementalItems[questionId];
            if (currentQuestionEntry) {
                dispatch(setApplicationValue({
                    name: `supplementalItems.${questionId}`,
                    value: {
                        ...currentQuestionEntry,
                        documentReviewStatus: result.documentReviewStatus || 'underReview'
                    }
                }));
            }
            
            // Reset hasChanges flag after successful resubmit
            dispatch(setSettingsValue({
                name: 'supplementalDocumentsHasChanges',
                value: false
            }));
            
            snackbar.success(intl.formatMessage({ id: 'supplementQuestionResubmitSuccess' }));
        } catch (error) {
            snackbar.error(intl.formatMessage({ id: 'supplementQuestionResubmitError' }));
            throw error;
        }
    }, [applicationId, api, supplementalItems, dispatch, snackbar, intl]);

    const confirmDeleteFile = useCallback(async () => {
        if (!fileToDelete || !applicationId) return;

        const { documentId, questionId } = fileToDelete;
        setIsDeleting(true);
        let deleteResult = null;

        try {
            deleteResult = await api.supplementalItems.deleteSubmittedFile(applicationId, documentId);
            const edmFileId = deleteResult?.edmFileId || fileToDelete.edmFileId || fileToDelete.fileId || fileToDelete.id;
            if (edmFileId && api?.edm?.deleteFile) {
                try {
                    await api.edm.deleteFile(edmFileId);
                } catch (_edmErr) {
                    // Silently handle EDM deletion errors
                }
            }
            dispatch(setSettingsValue({
                name: 'supplementalDocumentsHasChanges',
                value: true
            }));
            snackbar.success(
                intl.formatMessage({ id: 'fileDeletedSuccess' })
            );
        } catch (_error) {
            snackbar.error(
                intl.formatMessage({ id: 'fileDeletedError' })
            );
        } finally {
            const currentQuestionEntry = supplementalItems[questionId];
            if (currentQuestionEntry?.files) {
                const updatedFiles = currentQuestionEntry.files.map(file => 
                    file.documentId === documentId 
                        ? { ...file, status: 'deleted' }
                        : file
                );
                
                const newStatus = deleteResult?.documentReviewStatus || currentQuestionEntry.documentReviewStatus;
                
                dispatch(setApplicationValue({ 
                    name: `supplementalItems.${questionId}`, 
                    value: {
                        ...currentQuestionEntry,
                        files: updatedFiles,
                        documentReviewStatus: newStatus
                    }
                }));
            }
            setIsDeleting(false);
            closeDeleteDialog();
        }
    }, [fileToDelete, applicationId, api, supplementalItems, dispatch, snackbar, intl, closeDeleteDialog]);

    const hasQuestionGroups = questionGroups.length > 0;

    return (
        <div className={classes.root} role="region" aria-labelledby="submitted-documents-heading">
            <Typography variant="h2" id="submitted-documents-heading">
                <FormattedMessage id="submittedDocuments" />
            </Typography>
            <Divider className={classes.pageDivider} />
            {!hasQuestionGroups &&
                <EmptyState />
            }
            {hasQuestionGroups && !isMobile &&
                <DocumentGroupTable
                    groups={questionGroups}
                    expandedRows={expandedRows}
                    onRowClick={handleRowClick}
                    onDeleteFile={handleDeleteFile}
                    onViewFile={handleViewFile}
                    onUploadFile={handleUploadFile}
                    onSendMessage={handleSendMessage}
                    onResubmitQuestion={handleResubmitQuestion}
                    locale={selectedLocale}
                    hasChanges={hasChanges}
                />
            }
            {hasQuestionGroups && isMobile &&
                <DocumentGroupCards
                    groups={questionGroups}
                    expandedRows={expandedRows}
                    onRowClick={handleRowClick}
                    onDeleteFile={handleDeleteFile}
                    onViewFile={handleViewFile}
                    onUploadFile={handleUploadFile}
                    onSendMessage={handleSendMessage}
                    onResubmitQuestion={handleResubmitQuestion}
                    locale={selectedLocale}
                    hasChanges={hasChanges}
                />
            }

            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    <FormattedMessage id="deleteFileTitle" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        <FormattedMessage 
                            id="deleteFileConfirmationTitleTextId"
                        />
                        {fileToDelete && (
                            <Typography component="p" style={{ marginTop: '1rem', fontWeight: 'bold' }}>
                                {fileToDelete.name || fileToDelete.originalName || fileToDelete.documentId}
                            </Typography>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={closeDeleteDialog} 
                        disabled={isDeleting}
                    >
                        <FormattedMessage id="no" />
                    </Button>
                    <Button 
                        onClick={confirmDeleteFile} 
                        color="secondary"
                        disabled={isDeleting}
                    >
                        <FormattedMessage id="yes" />
                    </Button>
                </DialogActions>
            </Dialog>
            <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                onClick={(e) => {
                    e.currentTarget.value = '';
                }}
                style={{ display: 'none' }}
                accept=".pdf"
                aria-hidden="true"
                tabIndex={-1}
                aria-label={intl.formatMessage({ id: 'supportingDocumentsUploadFile', defaultMessage: 'Upload file' })}
            />
        </div>
    );
};

export default SubmittedDocuments;
